import { useEffect, useState } from "react";
import { DeviceMobile, X } from "@phosphor-icons/react";

/**
 * Botão flutuante "Instalar App" (PWA).
 * Aparece automaticamente quando o navegador dispara beforeinstallprompt.
 * Persiste "dispensa por 7 dias" no localStorage para não aborrecer o usuário.
 */
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export default function InstallPWA() {
  const [prompt, setPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [ios, setIos] = useState(false);
  const [guia, setGuia] = useState(false);

  useEffect(() => {
    // Já instalado (standalone)?
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) {
      setInstalled(true);
      return;
    }
    // Dispensado recentemente? (7 dias)
    // Nota: se o usuário quiser forçar, basta remover a key `pwa_install_dismissed_at` no DevTools
    const dismissedAt = parseInt(localStorage.getItem("pwa_install_dismissed_at") || "0", 10);
    if (dismissedAt && Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;

    if (isIOS()) {
      // Safari/iOS não dispara beforeinstallprompt: instalação é manual via Compartilhar → Adicionar à Tela de Início
      setIos(true);
      setShow(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setPrompt(e);
      setShow(true);
    };
    const installedHandler = () => {
      setInstalled(true);
      setShow(false);
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", installedHandler);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const install = async () => {
    if (ios) { setGuia(true); return; }
    if (!prompt) return;
    prompt.prompt();
    await prompt.userChoice;
    setPrompt(null);
    setShow(false);
  };

  const dismiss = () => {
    localStorage.setItem("pwa_install_dismissed_at", Date.now().toString());
    setShow(false);
    setGuia(false);
  };

  if (installed || !show) return null;

  if (guia) {
    return (
      <div data-testid="pwa-ios-guia" style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(10,10,10,0.72)", display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={() => setGuia(false)}>
        <div style={{ background: "#FFF", color: "#0A0A0A", borderRadius: "12px 12px 0 0", padding: "20px 20px 28px", width: "100%", maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
          <div style={{ fontWeight: 800, fontSize: "1.05rem", marginBottom: 6 }}>Instalar no iPhone / iPad</div>
          <div style={{ fontSize: "0.85rem", color: "#52525B", marginBottom: 14 }}>No iPhone a instalação é feita pelo Safari, em 3 toques:</div>
          <ol style={{ margin: 0, paddingLeft: 20, display: "grid", gap: 10, fontSize: "0.92rem", lineHeight: 1.35 }}>
            <li>Abra este site no <strong>Safari</strong> (não funciona dentro do WhatsApp ou de outro navegador).</li>
            <li>Toque no botão <strong>Compartilhar</strong> <span style={{ display: "inline-block", border: "1px solid #D4D4D8", borderRadius: 4, padding: "0 6px", fontSize: "0.8rem" }}>⬆︎</span> na barra de baixo (ou de cima, no iPad).</li>
            <li>Role a lista e toque em <strong>"Adicionar à Tela de Início"</strong> → <strong>Adicionar</strong>.</li>
          </ol>
          <div style={{ fontSize: "0.8rem", color: "#52525B", marginTop: 14 }}>O ícone <strong>OSTecnoVias</strong> aparece na tela inicial e abre em tela cheia, como um app.</div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => setGuia(false)} data-testid="pwa-ios-guia-ok">Entendi</button>
            <button className="btn btn-outline" onClick={dismiss} data-testid="pwa-ios-guia-depois">Agora não</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="pwa-install-banner"
      style={{
        position: "fixed",
        bottom: window.innerWidth <= 768 ? 92 : 20,
        left: 20,
        right: 20,
        maxWidth: 420,
        margin: "0 auto",
        background: "#0A0A0A",
        color: "#FFF",
        borderRadius: 8,
        padding: 14,
        boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        gap: 12,
        zIndex: 40,
        borderLeft: "4px solid #FFCC00",
      }}
    >
      <DeviceMobile size={28} weight="bold" color="#FFCC00" />
      <div style={{ flex: 1, lineHeight: 1.3 }}>
        <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>Instalar OSTecnoVias</div>
        <div style={{ fontSize: "0.75rem", opacity: 0.85 }}>{ios ? "Compartilhar → Adicionar à Tela de Início" : "Acesso rápido pela tela do celular"}</div>
      </div>
      <button
        onClick={install}
        data-testid="pwa-install-btn"
        style={{
          background: "#FFCC00",
          color: "#0A0A0A",
          border: "none",
          padding: "8px 14px",
          borderRadius: 4,
          fontWeight: 700,
          fontSize: "0.8rem",
          cursor: "pointer",
          letterSpacing: "0.02em",
        }}
      >
        {ios ? "Como instalar" : "Instalar"}
      </button>
      <button
        onClick={dismiss}
        aria-label="Dispensar"
        style={{ background: "transparent", border: "none", color: "#A1A1AA", cursor: "pointer", padding: 4 }}
      >
        <X size={16} weight="bold" />
      </button>
    </div>
  );
}
